import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "personal";

export type Personal = {
  readonly id: string;
  readonly vorname: string;
  readonly nachname: string;
  readonly interneNummer?: string;
  readonly email?: string;
  readonly mobilTelefon?: string;
  readonly festnetzTelefon?: string;
  readonly geburtsdatum?: string;
  readonly notizen?: string;
  readonly istAtemschutzgeraetetraeger?: boolean;
  readonly aktuelleFitness?: string;
  readonly letzteG263Untersuchung?: string;
  readonly g263GueltigkeitsJahre?: number;
  readonly letzterLehrgangsabschluss?: string;
  readonly letzteUebungOderEinsatz?: string;
  /** Funktion "Einsatzleitung" — vorgeschlagen im Einsatzformular. */
  readonly einsatzleitung?: boolean;
  /** Funktion "Protokollierung" — vorgeschlagen im Einsatzformular. */
  readonly protokollierung?: boolean;
  readonly qualifikationen?: ReadonlyArray<string>;
};

function usePersonalSyncModule(): SyncModule<ReadonlyArray<Personal>> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Personnel is a new-Datenportal-only resource — the legacy portal has
      // no equivalent endpoint, so we simply don't fetch anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchPersonalNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchPersonalNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Personal>> {
  const response = await fetch(`${baseUrl}/api/device/sync/personnel`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.personnel ?? []).map(parsePersonalNew);
}

function parsePersonalNew(entry: any): Personal {
  return {
    id: entry.id,
    vorname: entry.firstName,
    nachname: entry.lastName,
    interneNummer: entry.internalNumber || undefined,
    email: entry.email || undefined,
    mobilTelefon: entry.mobilePhone || undefined,
    festnetzTelefon: entry.landlinePhone || undefined,
    geburtsdatum: entry.dateOfBirth || undefined,
    notizen: entry.notes || undefined,
    istAtemschutzgeraetetraeger: entry.isRespiratoryProtection ?? false,
    aktuelleFitness: entry.currentFitness || undefined,
    letzteG263Untersuchung: entry.lastG26_3Date || undefined,
    g263GueltigkeitsJahre: entry.g26_3ValidityYears ?? undefined,
    letzterLehrgangsabschluss: entry.lastCourseCompletionDate || undefined,
    letzteUebungOderEinsatz: entry.lastExerciseOrOperation || undefined,
    einsatzleitung: entry.isIncidentCommand ?? false,
    protokollierung: entry.isProtocolKeeper ?? false,
    qualifikationen: Array.isArray(entry.qualifications)
      ? entry.qualifications
      : [],
  };
}

export function usePersonalSync() {
  const module = usePersonalSyncModule();
  useSyncModuleRegistration(module);
}

export function usePersonal(): ReadonlyArray<Personal> {
  return useSyncData(syncModuleId) ?? [];
}
